import { FC, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import ImgPopular from '@/assets/images/lp/entry/icon_popular.svg';
import ImgBtm from '@/assets/images/lp/entry/img_btm.png';
import ImgBtmWebp from '@/assets/images/lp/entry/img_btm.png?webp';
import ImgBtm2x from '@/assets/images/lp/entry/img_btm@2x.png';
import ImgBtm2xWebp from '@/assets/images/lp/entry/img_btm@2x.png?webp';
import ImgCampaignFlowBonusPC from '@/assets/images/lp/entry/img_campaign_flow_bonus_pc.png';
import ImgCampaignFlowBonusPCWebp from '@/assets/images/lp/entry/img_campaign_flow_bonus_pc.png?webp';
import ImgCampaignFlowBonusSPWebp from '@/assets/images/lp/entry/img_campaign_flow_bonus_sp.png?webp';
import ImgCampaignFlowUpdMissionPC from '@/assets/images/lp/entry/img_campaign_flow_upd_mission_pc.png';
import ImgCampaignFlowUpdMissionPCWebp from '@/assets/images/lp/entry/img_campaign_flow_upd_mission_pc.png?webp';
import ImgCampaignFlowUpdMissionSPWebp from '@/assets/images/lp/entry/img_campaign_flow_upd_mission_sp.png?webp';
import ImgCharm1 from '@/assets/images/lp/entry/img_charm_001.png';
import ImgCharm2 from '@/assets/images/lp/entry/img_charm_002.png';
import ImgCharm3 from '@/assets/images/lp/entry/img_charm_003.png';
import ImgCharm3Webp from '@/assets/images/lp/entry/img_charm_003.png?webp';
import ImgCharm3_2x from '@/assets/images/lp/entry/img_charm_003@2x.png';
import ImgCharm3_2xWebp from '@/assets/images/lp/entry/img_charm_003@2x.png?webp';
import ImgCharm4 from '@/assets/images/lp/entry/img_charm_004.png';
import ImgCharm4Webp from '@/assets/images/lp/entry/img_charm_004.png?webp';
import ImgCharm4_2x from '@/assets/images/lp/entry/img_charm_004@2x.png';
import ImgCharm4_2xWebp from '@/assets/images/lp/entry/img_charm_004@2x.png?webp';
import ImgMerit1 from '@/assets/images/lp/entry/img_merit_001.svg';
import ImgMerit2 from '@/assets/images/lp/entry/img_merit_002.svg';
import ImgStep1 from '@/assets/images/lp/entry/img_step_1.svg';
import ImgStep2 from '@/assets/images/lp/entry/img_step_2.svg';
import ImgStep3 from '@/assets/images/lp/entry/img_step_3.svg';
import FooterLog from '@/assets/images/lp/entry/logo_footer.svg';
import HeaderLogo from '@/assets/images/lp/entry/logo_header.svg';
import AppealText from '@/components/elements/AB/AppealText';
import Image from '@/components/elements/Image';
import { dayjst } from '@/lib/dayjst';
import { breakPoints } from '@/utility/mediaQuery';

import {
  AboutArea,
  AboutCampaignCautionWrapper,
  AboutCampaignContent,
  AboutCampaignHeadLine,
  AboutCampaignSection,
  AboutCampaignTitle,
  AboutCampaignWrapper,
  AboutDescription,
  BottomMainVisualSection,
  ButtonAppealText,
  ButtonSection,
  CampaignImgWrapper,
  CampaignSection,
  CharmCaution,
  CharmContent,
  CharmDescription,
  CharmHeadLine,
  CharmHeadlineWrapper,
  CharmPopularImg,
  CharmSection,
  CharmSubHeadline,
  CharmWrapper,
  CopyRight,
  FloatingArea,
  Footer,
  FooterLink,
  FooterLinkList,
  FooterLogoImg,
  FooterLogoLink,
  Header,
  HeaderLogoImg,
  HeaderLogoLink,
  LinkButton,
  Main,
  MainVisualChargeBouns,
  MainVisualMissionUpdated,
  MeritContent,
  MeritDescription,
  MeritHeadLine,
  MeritSection,
  MeritWrapper,
  NotCoveredWrapper,
  StepAfterAgeConfirm,
  StepArrowIcon,
  StepComplete,
  StepContent,
  StepContentBody,
  StepDescription,
  StepDotIcon,
  StepDotIconWrapper,
  StepHeadLine,
  StepRedHeadLine,
  StepSection,
  StepWrapper,
  Title,
} from './style';

interface LpTemplateProps {
  inviter: string | null;
  isChargeBounsCp: boolean;
}

const LpTemplate: FC<LpTemplateProps> = ({ inviter, isChargeBounsCp }) => {
  const [ref, inView] = useInView({
    threshold: 0,
  });

  const signUpUrl = inviter ? `/signup?inviter=${inviter}` : '/signup';

  const missionData = useMemo(
    () => ({
      srcSet: `${
        isChargeBounsCp ? ImgCampaignFlowBonusPC : ImgCampaignFlowUpdMissionPC
      }`,
      mediaSet: [
        {
          srcSet: `${
            isChargeBounsCp
              ? ImgCampaignFlowBonusPCWebp
              : ImgCampaignFlowUpdMissionPCWebp
          }`,
          media: `(min-width: ${breakPoints.large}px)`,
          type: 'image/webp',
        },
        {
          srcSet: `${
            isChargeBounsCp
              ? ImgCampaignFlowBonusPCWebp
              : ImgCampaignFlowUpdMissionPCWebp
          }`,
          media: `(min-width: ${breakPoints.medium}px)`,
        },
        {
          srcSet: `${
            isChargeBounsCp
              ? ImgCampaignFlowBonusSPWebp
              : ImgCampaignFlowUpdMissionSPWebp
          }`,
          type: 'image/webp',
        },
      ],
      alt: isChargeBounsCp
        ? '新規登録で投票に使えるポイント1,000円分+今ならミッション達成で最大3,000円分+初回チャージで1,000円分→合計5,000円分ポイントプレゼント'
        : '新規登録で投票に使えるポイント1,000円分+今ならミッション達成で最大3,000円分→合計4,000円分ポイントプレゼント',
    }),
    [],
  );

  const bounsPeriodText = useMemo(() => {
    if (isChargeBounsCp) {
      const since = dayjst(import.meta.env.VITE_TORETA_BOUNS_CAMPAIGN_SINCE);
      const until = dayjst(import.meta.env.VITE_TORETA_BOUNS_CAMPAIGN_UNTIL);

      if (since && until) {
        return `${since.format('YYYY/MM/DD')}（${since.format(
          'ddd',
        )}）${since.format('HH:mm')}～${until.format(
          'YYYY/MM/DD',
        )}（${until.format('ddd')}）${until.format('HH:mm:ss')}`;
      }
    }
    return '';
  }, [isChargeBounsCp]);

  return (
    <>
      <Header>
        <HeaderLogoLink to="/">
          <HeaderLogoImg
            width="110"
            height="28"
            alt="DMM競輪"
            src={HeaderLogo}
          />
        </HeaderLogoLink>
      </Header>
      <Main>
        {isChargeBounsCp ? (
          <MainVisualChargeBouns />
        ) : (
          <MainVisualMissionUpdated />
        )}

        <ButtonSection ref={ref}>
          <ButtonAppealText>
            <AppealText defaultText="\ 最短10分で登録完了 /" />
          </ButtonAppealText>
          <LinkButton to={signUpUrl}>今すぐ無料登録！</LinkButton>
        </ButtonSection>

        <CharmSection>
          <Title>DMM競輪の魅力</Title>
          <CharmWrapper>
            <CharmContent>
              <Image
                srcSet={ImgCharm1}
                width="100%"
                height={['134px', '134px', '134px', '220px']}
                alt="画像の差し替え: お得なチャージバック"
              />
              <div>
                <CharmHeadlineWrapper>
                  <CharmHeadLine>お得なチャージバック</CharmHeadLine>
                </CharmHeadlineWrapper>
                <CharmDescription>
                  投票金額に応じて、毎月3回{' '}
                  <span>toreta+ をチャージバック</span>
                  。投票すればするほどお得に競輪を楽しめます。
                </CharmDescription>
              </div>
            </CharmContent>
            <CharmContent>
              <Image
                srcSet={ImgCharm2}
                width="100%"
                height={['134px', '134px', '134px', '220px']}
                alt="画像の差し替え: 魅力的なキャンペーン"
              />
              <div>
                <CharmHeadlineWrapper>
                  <CharmHeadLine>魅力的なキャンペーン</CharmHeadLine>
                </CharmHeadlineWrapper>
                <CharmDescription>
                  チャージバックの他にも、お得なキャンペーンが盛りだくさん！
                  投票に使えるポイントやグッズをプレゼントしています。
                </CharmDescription>
              </div>
            </CharmContent>
            <CharmContent>
              <Image
                srcSet={`${ImgCharm3} 1x, ${ImgCharm3_2x} 2x`}
                mediaSet={[
                  {
                    srcSet: `${ImgCharm3Webp} 1x, ${ImgCharm3_2xWebp} 2x`,
                    type: 'image/webp',
                  },
                ]}
                width="100%"
                height={['134px', '134px', '134px', '220px']}
                alt="選手メモ機能"
              />
              <div>
                <CharmHeadlineWrapper>
                  <CharmSubHeadline>DMM競輪 オリジナル</CharmSubHeadline>
                  <CharmHeadLine>選手メモ機能</CharmHeadLine>
                  <CharmPopularImg src={ImgPopular} alt="大好評" />
                </CharmHeadlineWrapper>
                <CharmDescription>
                  選手一人ひとりに対して自分だけの情報を無制限で記録できます。メモはいつでも参照できます。
                </CharmDescription>
              </div>
            </CharmContent>
            <CharmContent>
              <Image
                srcSet={`${ImgCharm4} 1x, ${ImgCharm4_2x} 2x`}
                mediaSet={[
                  {
                    srcSet: `${ImgCharm4Webp} 1x, ${ImgCharm4_2xWebp} 2x`,
                    type: 'image/webp',
                  },
                ]}
                width="100%"
                height={['134px', '134px', '134px', '220px']}
                alt="投票成績・収支分析機能"
              />
              <div>
                <CharmHeadlineWrapper>
                  <CharmHeadLine>投票成績・収支分析機能</CharmHeadLine>
                </CharmHeadlineWrapper>
                <CharmDescription>
                  収支推移をグラフで見ることができます。また、投票額・払戻額・回収率などの成績をひと目で確認できるので投票成績の分析に役立ちます。
                </CharmDescription>
              </div>
            </CharmContent>
          </CharmWrapper>
          <CharmCaution>
            ※画面のデザインは最新のものと異なる場合があります
          </CharmCaution>
        </CharmSection>

        <MeritSection>
          <Title>今すぐはじめよう</Title>
          <MeritWrapper>
            <MeritContent>
              <Image
                srcSet={ImgMerit1}
                width={['272px', '272px', '279px', '279px']}
                height="144px"
                alt="最短即日で利用可能"
              />
              <div>
                <MeritHeadLine>最短即日で利用可能</MeritHeadLine>
                <MeritDescription>
                  DMM競輪のご利用には本人確認が必要となります。本人確認は最短即日で完了します。
                </MeritDescription>
              </div>
            </MeritContent>
            <MeritContent>
              <Image
                srcSet={ImgMerit2}
                width={['272px', '272px', '279px', '279px']}
                height="144px"
                alt="様々な決済手段に対応"
              />
              <div>
                <MeritHeadLine>様々な決済手段に対応</MeritHeadLine>
                <MeritDescription>
                  投票に必要な電子マネー「toreta+（トレタプラス）」のチャージは
                  スマホ決済（PayPay・メルペイ）・クレジットカード・コンビニ・銀行振込に対応しています。
                </MeritDescription>
              </div>
            </MeritContent>
          </MeritWrapper>
        </MeritSection>

        <StepSection>
          <StepRedHeadLine>最短5分で利用開始！</StepRedHeadLine>
          <Title>ご登録のながれ</Title>
          <StepWrapper>
            <StepContent>
              <Image
                srcSet={ImgStep1}
                width={['72px', '72px', '184px', '184px']}
                height={['76px', '76px', '195px', '195px']}
                alt="STEP1"
              />
              <StepContentBody>
                <StepHeadLine>DMM会員登録</StepHeadLine>
                <StepDescription>まずはDMMアカウントを登録</StepDescription>
              </StepContentBody>
            </StepContent>
            <StepDotIconWrapper>
              <StepDotIcon />
            </StepDotIconWrapper>
            <StepContent>
              <Image
                srcSet={ImgStep2}
                width={['72px', '72px', '184px', '184px']}
                height={['76px', '76px', '195px', '195px']}
                alt="STEP2"
              />
              <StepContentBody>
                <StepHeadLine>情報入力</StepHeadLine>
                <StepDescription>カンタンな情報入力だけでOK</StepDescription>
              </StepContentBody>
            </StepContent>
            <StepDotIconWrapper>
              <StepDotIcon />
            </StepDotIconWrapper>
            <StepContent>
              <Image
                srcSet={ImgStep3}
                width={['72px', '72px', '184px', '184px']}
                height={['76px', '76px', '195px', '195px']}
                alt="STEP3"
              />
              <StepContentBody>
                <StepHeadLine>本人確認</StepHeadLine>
                <StepDescription>
                  本人確認は最短即日で
                  <br />
                  完了
                </StepDescription>
              </StepContentBody>
            </StepContent>
          </StepWrapper>
          <StepArrowIcon />
          <StepAfterAgeConfirm>本人確認が承認されると</StepAfterAgeConfirm>
          <StepComplete>
            登録完了
            <br />
            ポイントGET！
          </StepComplete>
        </StepSection>

        <CampaignSection>
          <Title>新規登録キャンペーン＋ミッション達成で</Title>
          <CampaignImgWrapper>
            <Image
              srcSet={missionData.srcSet}
              mediaSet={missionData.mediaSet}
              alt={missionData.alt}
            />
          </CampaignImgWrapper>
        </CampaignSection>

        <AboutArea>
          <Title>DMM競輪とは</Title>
          <AboutDescription>
            DMM競輪は、スマートフォンやパソコンでいつでも・どこでも・カンタンに
            <br />
            競輪投票やチャリロト・Dokanto!の購入ができるサービスです
          </AboutDescription>
        </AboutArea>

        <BottomMainVisualSection>
          <Image
            srcSet={`${ImgBtm} 1x, ${ImgBtm2x} 2x`}
            mediaSet={[
              {
                srcSet: `${ImgBtmWebp} 1x, ${ImgBtm2xWebp} 2x`,
                type: 'image/webp',
              },
            ]}
            height={['228px', '228px', '336px', '336px']}
            alt="インターネットで簡単競輪投票！ DMM競輪"
          />
        </BottomMainVisualSection>

        <AboutCampaignSection>
          <AboutCampaignTitle>新規登録キャンペーンについて</AboutCampaignTitle>
          <AboutCampaignWrapper>
            <AboutCampaignHeadLine>キャンペーン概要</AboutCampaignHeadLine>
            <AboutCampaignContent marginBottom="l">
              初めてDMM競輪への利用登録が完了された方全員へ
              1,000円分のtoreta+（トレタプラス）をプレゼントいたします。
              {isChargeBounsCp && (
                <>
                  <br />
                  <br />
                  <b>
                    ＜{bounsPeriodText} の期間に新規登録した方への限定特典＞
                  </b>
                  <br />
                  1,000円以上のtoreta+ チャージをした方全員へ1,000円分のtoreta+
                  をプレゼントいたします。
                  <br />
                  ※新規登録日から30日以内にtoreta+チャージを行わないと権利は無効となります。
                  <br />
                  ※1,000円以上のtoreta+ チャージを行ってから24時間以内にtoreta+
                  付与を行います。
                </>
              )}
            </AboutCampaignContent>
            <AboutCampaignHeadLine>対象者</AboutCampaignHeadLine>
            <AboutCampaignContent marginBottom="l">
              DMM競輪へ新規登録をした方
              <br />
              ※すでにDMM競輪にご登録済みの方、過去にDMM競輪を一度でも利用したことがある方は対象外となります。
            </AboutCampaignContent>
            <AboutCampaignHeadLine>注意事項</AboutCampaignHeadLine>
            <AboutCampaignCautionWrapper>
              <li>
                本キャンペーンでプレゼントするtoreta+
                はDMM競輪でのみご利用いただけます。
              </li>
              <li>
                本キャンペーンでプレゼントするtoreta+
                の有効期限は付与日から30日間です。
              </li>
              <li>
                以下の場合、プレゼント対象となりませんのでご注意ください。
                <NotCoveredWrapper>
                  <li>toreta+ 付与までに利用停止となった場合</li>
                  <li>規約違反行為等により退会処分となった場合</li>
                  <li>利用規約・サイトポリシーに違反する行為を行った場合</li>
                  <li>
                    特典を目的とし、DMMを故意に退会、そのほか不正な登録等が行われたと判断された場合
                  </li>
                </NotCoveredWrapper>
              </li>
              <li>
                本キャンペーンは予告なく変更・終了する可能性がございます。あらかじめご了承ください。
              </li>
            </AboutCampaignCautionWrapper>
          </AboutCampaignWrapper>
          <AboutCampaignTitle>
            新規ユーザー限定 ミッションについて
          </AboutCampaignTitle>
          <AboutCampaignWrapper>
            <AboutCampaignHeadLine>キャンペーン概要</AboutCampaignHeadLine>
            <AboutCampaignContent marginBottom="l">
              初めてDMM競輪へ利用登録が完了された方限定のミッションをご用意しております。
              <br />
              ミッションクリアでもれなくtoreta+ をプレゼントいたします。
              <br />
              ミッションの詳細はDMM競輪へのご登録完了後、キャンペーンページをご確認ください。
            </AboutCampaignContent>
          </AboutCampaignWrapper>
        </AboutCampaignSection>
      </Main>

      <Footer>
        <FooterLogoLink to="/">
          <FooterLogoImg
            width="125.29"
            height="32"
            alt="DMM競輪"
            src={FooterLog}
            loading="lazy"
          />
        </FooterLogoLink>
        <FooterLink>
          <FooterLinkList>
            <li>
              <a
                href="https://dmm-corp.com/company/"
                target="_blank"
                rel="noreferrer"
              >
                会社概要
              </a>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_DMM_TERMS_URL}/privacy/`}
                target="_blank"
                rel="noreferrer"
              >
                個人情報保護に関して
              </a>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_DMM_TERMS_URL}/keirin_tokushou/`}
                target="_blank"
                rel="noreferrer"
              >
                特定商取引法に基づく表示
              </a>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_DMM_TERMS_URL}/chariloto/`}
                target="_blank"
                rel="noreferrer"
              >
                DMM競輪利用規約
              </a>
            </li>
          </FooterLinkList>
        </FooterLink>
        <CopyRight>Copyright © since 1998 DMM All Rights Reserved.</CopyRight>
      </Footer>

      <FloatingArea isShow={!inView}>
        <ButtonAppealText color="white">
          <AppealText defaultText="\ 最短10分で登録完了 /" />
        </ButtonAppealText>
        <LinkButton to={signUpUrl}>今すぐ無料登録！</LinkButton>
      </FloatingArea>
    </>
  );
};

export default LpTemplate;
